import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Alert,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import api from '../api';
import Spinner from '../components/Spinner';
import { useStaff } from '../contexts/StaffContext';

const getTermEndDate = (startDate?: string, endDate?: string, durationMonths?: number) => {
  if (endDate) {
    const result = new Date(endDate);
    result.setHours(23, 59, 59, 999);
    return result;
  }

  const monthCount = Number(durationMonths ?? 0);
  if (startDate && Number.isFinite(monthCount) && monthCount > 0) {
    const result = new Date(startDate);
    result.setHours(0, 0, 0, 0);
    result.setMonth(result.getMonth() + monthCount);
    result.setHours(23, 59, 59, 999);
    return result;
  }
  return null;
};

const isTermEnded = (startDate?: string, endDate?: string, durationMonths?: number) => {
  const end = getTermEndDate(startDate, endDate, durationMonths);
  return end ? Date.now() > end.getTime() : false;
};

const StaffTermHistory: React.FC = () => {
  const navigate = useNavigate();
  const { staff, isLoading: loading } = useStaff();
  const [loadingResults, setLoadingResults] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [submissions, setSubmissions] = useState<any[]>([]);
  const [termNames, setTermNames] = useState<string[]>([]);
  const [selectedTerm, setSelectedTerm] = useState('');

  const staffSchool = staff && typeof staff.school === 'object' ? staff.school : null;
  const activeTermName = staffSchool?.activeTermName || '';
  const activeTermEnded = isTermEnded(staffSchool?.activeTermStartDate, staffSchool?.activeTermEndDate, staffSchool?.termDurationMonths);
  const activeTermEndText = activeTermEnded ? 'Term has ended' : activeTermName ? `Available after ${getTermEndDate(staffSchool?.activeTermStartDate, staffSchool?.activeTermEndDate, staffSchool?.termDurationMonths)?.toLocaleDateString()}` : 'Pending term end date configuration';
  const selectedActiveTerm = selectedTerm === activeTermName && activeTermName;
  const showPendingTermInfo = selectedActiveTerm && !activeTermEnded;
  const showEarlyHistoryInfo = !activeTermEnded && !selectedTerm && !termNames.length && !submissions.length;

  useEffect(() => {
    const fetchSubmissions = async () => {
      if (!staff?._id) return;
      setLoadingResults(true);
      setError(null);
      try {
        const termQuery = selectedTerm ? `?termName=${encodeURIComponent(selectedTerm)}` : '';
        const response = await api.get(`/staff/term-history${termQuery}`);
        const data = response.data || {};
        setSubmissions(Array.isArray(data.submissions) ? data.submissions : []);
        setTermNames(Array.isArray(data.termNames) ? data.termNames : []);
      } catch (err: any) {
        setError(err.response?.data?.message || 'Failed to load staff term history');
      } finally {
        setLoadingResults(false);
      }
    };

    fetchSubmissions();
  }, [staff?._id, selectedTerm]);

  const termSummary = useMemo(() => {
    const classSet = new Set<string>();
    const studentSet = new Set<string>();
    let eligibleCount = 0;

    submissions.forEach((submission) => {
      classSet.add(submission.className || submission.student?.className || 'Unknown');
      const studentId = submission.student?._id || submission.student;
      if (studentId) studentSet.add(studentId.toString());
      if (submission.isEligibleForExam) eligibleCount += 1;
    });

    return {
      classCount: classSet.size,
      studentCount: studentSet.size,
      eligibleCount,
      submissionCount: submissions.length
    };
  }, [submissions]);

  if (loading || loadingResults) {
    return (
      <Box display="flex" justifyContent="center" mt={4}>
        <Spinner />
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4, flexWrap: 'wrap', gap: 2 }}>
        <Typography variant="h4" sx={{ fontWeight: 600 }}>
          Staff Term History
        </Typography>
        <Button variant="outlined" onClick={() => navigate('/staff/dashboard')}>
          Back to Dashboard
        </Button>
      </Box>

      <Paper sx={{ p: 3, mb: 4 }}>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems="center" justifyContent="space-between">
          <Box>
            <Typography variant="subtitle2" color="text.secondary">Selected Term</Typography>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>{selectedTerm || 'All Terms'}</Typography>
          </Box>
          <Box>
            <Typography variant="subtitle2" color="text.secondary">Total Submissions</Typography>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>{termSummary.submissionCount}</Typography>
          </Box>
          <Box>
            <Typography variant="subtitle2" color="text.secondary">Classes Covered</Typography>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>{termSummary.classCount}</Typography>
          </Box>
          <Box>
            <Typography variant="subtitle2" color="text.secondary">Students</Typography>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>{termSummary.studentCount}</Typography>
          </Box>
          <Box>
            <Typography variant="subtitle2" color="text.secondary">Eligible Records</Typography>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>{termSummary.eligibleCount}</Typography>
          </Box>
        </Stack>
      </Paper>

      <Paper sx={{ p: 3, mb: 4 }}>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems="flex-end">
          <FormControl fullWidth sx={{ minWidth: 220 }}>
            <InputLabel id="filter-term-label">Term</InputLabel>
            <Select
              labelId="filter-term-label"
              label="Term"
              value={selectedTerm}
              onChange={(event) => setSelectedTerm(event.target.value)}
            >
              <MenuItem value="">All Terms</MenuItem>
              {termNames.map((termName) => (
                <MenuItem key={termName} value={termName}>
                  {termName}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Stack>
      </Paper>

      {showPendingTermInfo && (
        <Alert severity="info" sx={{ mb: 3 }}>
          Term history for the active term is available only after the current term ends. {activeTermEndText}
        </Alert>
      )}
      {showEarlyHistoryInfo && (
        <Alert severity="info" sx={{ mb: 3 }}>
          Term history is available after the current term ends. Completed term records will appear here once the term closes.
        </Alert>
      )}
      {error && <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>}

      {submissions.length === 0 ? (
        <Alert severity="info">No term history records available for the selected term.</Alert>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Student</TableCell>
                <TableCell>Class</TableCell>
                <TableCell>Term</TableCell>
                <TableCell>Teacher</TableCell>
                <TableCell>Quiz Score</TableCell>
                <TableCell>Assignment Score</TableCell>
                <TableCell>Attendance %</TableCell>
                <TableCell>Eligible</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {submissions.map((submission) => (
                <TableRow key={submission._id} hover>
                  <TableCell>{submission.student?.name || 'Unknown'}</TableCell>
                  <TableCell>{submission.className || submission.student?.className || 'Unknown'}</TableCell>
                  <TableCell>{submission.termName || 'Unknown'}</TableCell>
                  <TableCell>{submission.staff?.name || 'Unknown'}</TableCell>
                  <TableCell>{`${submission.quizScoreObtained || 0} / ${submission.quizScoreTotal || 0}`}</TableCell>
                  <TableCell>{`${submission.assignmentScoreObtained || 0} / ${submission.assignmentScoreTotal || 0}`}</TableCell>
                  <TableCell>{Number(submission.attendancePercentage ?? 0).toFixed(2)}%</TableCell>
                  <TableCell>{submission.isEligibleForExam ? 'Yes' : 'No'}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
};

export default StaffTermHistory;
